import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, isNil } from 'lodash';
import { AsyncDropdown } from 'taboola-ultimate-ui';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import fetchAccounts from '../../../../flows/fetchAccounts';
import fetchAccountsUnderNetwork from '../../../../flows/fetchAccountsUnderNetwork';
import { useDimension } from '../../../../hooks';
import { isNetworkAccountType } from '../../../../modules/account-management';
import ACCOUNT_TYPE, { isDSPAccount, isNetworkSubAccount } from '../../../../modules/account-management/accountType';
import { FormattedMessage } from '../../../../modules/taboola-common-frontend-modules/i18n';
import { accountsMapSelector } from '../../../../selectors/baseSelectors';
import { AccountItemIcon } from '../../../AccountPicker/AccountItemIcon/AccountItemIcon';
import { PARAMS_TO_KEEP } from '../../../AccountPicker/AccountPickerMenuItem/AccountPickerMenuItem';
import { useAsyncDropdownCache } from '../../hooks/useAsyncDropdownCache';
import { useBreadcrumbNavigation } from '../../hooks/useBreadcrumbNavigation';
import { usePickerTotal } from '../../hooks/usePickerTotal';
import { AccountPickerValueContainer } from '../AccountPickerValueContainer/AccountPickerValueContainer';
import { AccountBalance } from '../AccoutBalance/AccountBalance';
import { BreadcrumbsPickerStep } from '../BreadcrumbsPickerStep/BreadcrumbsPickerStep';
import { DropdownOptionRenderer } from '../DropdownOptionRenderer/DropdownOptionRenderer';
import { commonPickerDropdownStyle } from '../commonPickerDropdownStyle';
import commonStyles from '../commonPickerDropdownStyle.module.scss';
import styles from './BreadcrumbsAccountPicker.module.scss';

const ACCOUNT_PICKER_TOTAL_KEY = 'ACCOUNT_PICKER_TOTAL_KEY';

const OptionRenderer = ({ data, isRoot, ...rest }) => {
    return (
        <DropdownOptionRenderer
            data={data}
            icon={<AccountItemIcon {...data} hasOuterLink />}
            noIconPlaceholder={!isRoot}
            idPropName="id"
            namePropName="accountDescription"
            {...rest}
        />
    );
};

const getPreviewAccount = (selectedAccount, isRoot) => {
    if (isRoot) {
        if (isNetworkSubAccount(selectedAccount)) {
            return {
                id: selectedAccount.parentNetworkAccountId,
                accountName: selectedAccount.parentNetworkAccountName,
                accountDescription: selectedAccount.parentNetworkAccountDescription,
                type: ACCOUNT_TYPE.NETWORK,
            };
        }
        return selectedAccount;
    }

    if (isNetworkSubAccount(selectedAccount)) {
        return selectedAccount;
    }
};

export const BreadcrumbsAccountPicker = ({ isRoot }) => {
    const dispatch = useDispatch();
    const selectedAccount = useAccount();
    const previewAccount = useMemo(() => getPreviewAccount(selectedAccount, isRoot), [selectedAccount, isRoot]);
    const accountsMap = useSelector(accountsMapSelector);
    const showAccountIcon = isDSPAccount(previewAccount);
    const [dimension] = useDimension();

    const fetcher = useCallback(
        ({ searchText, pageSize, isRetry, onSuccess, onError, options, singleRequestCacheKey }) => {
            if (isRoot) {
                return dispatch(
                    fetchAccounts(searchText, pageSize, isRetry, onSuccess, onError, options, singleRequestCacheKey)
                );
            }
            const networkAccountName = isNetworkAccountType(selectedAccount)
                ? selectedAccount.accountName
                : selectedAccount.parentNetworkAccountName;
            return dispatch(
                fetchAccountsUnderNetwork(
                    searchText,
                    pageSize,
                    isRetry,
                    onSuccess,
                    onError,
                    options,
                    networkAccountName,
                    singleRequestCacheKey
                )
            );
        },
        [isRoot, selectedAccount, dispatch]
    );

    const dimensionTotalTrigger = isRoot ? null : dimension;
    const totalCount = usePickerTotal({
        fetcher,
        enabled: !isRoot && previewAccount?.id !== selectedAccount.id,
        queryKey: [ACCOUNT_PICKER_TOTAL_KEY, selectedAccount.id, dimensionTotalTrigger],
    });

    const allOptionsValue = isRoot
        ? null
        : {
              id: isNetworkAccountType(selectedAccount) ? selectedAccount?.id : selectedAccount.parentNetworkAccountId,
              accountDescription: <FormattedMessage id="app.breadcrumbs.all.accounts.option" />,
              hideId: true,
          };

    const commonAsyncDropdownProps = useAsyncDropdownCache({
        fetcher,
        allOptionsValue,
    });
    const { isOpen, closeDropdown, openDropdown, onInputChange } = commonAsyncDropdownProps;

    const accountValue = isNil(previewAccount?.id) ? (
        <FormattedMessage
            id="app.breadcrumbs.all.accounts.title"
            values={{ count: isNil(totalCount) ? '' : `(${totalCount})` }}
        />
    ) : (
        <span className={styles['description']}>
            {showAccountIcon && <AccountItemIcon {...selectedAccount} className={styles['account-icon-wrapper']} />}
            <span>
                {previewAccount.accountDescription ??
                    get(accountsMap, `${previewAccount?.id}.accountDescription`) ??
                    ''}
            </span>
        </span>
    );
    const OptionRendererCustomIcon = useCallback(props => <OptionRenderer {...props} isRoot={isRoot} />, [isRoot]);

    const handleSelectedOption = useBreadcrumbNavigation({
        paramsToKeep: PARAMS_TO_KEEP,
        closeDropdown,
        selectedItem: selectedAccount,
    });

    // Dropdown cache cleaning
    useEffect(() => {
        if (isRoot) {
            return;
        }
        onInputChange('');
    }, [isRoot, dimensionTotalTrigger, onInputChange, selectedAccount?.accountName]);

    return (
        <BreadcrumbsPickerStep
            label={
                <>
                    <FormattedMessage
                        id={
                            isNetworkAccountType(previewAccount?.type)
                                ? 'app.breadcrumbs.account.network.title'
                                : 'app.breadcrumbs.account.title'
                        }
                        className={commonStyles['label']}
                    />
                    {previewAccount?.id === selectedAccount.id ? <AccountBalance /> : null}
                </>
            }
            value={accountValue}
            isOpen={isOpen}
            openDropdown={openDropdown}
            dataAutomationId={isNetworkAccountType(previewAccount?.type) ? 'root-account-picker' : 'account-picker'}
            closeDropdown={closeDropdown}
        >
            <AsyncDropdown
                {...commonAsyncDropdownProps}
                styles={commonPickerDropdownStyle}
                optionRenderer={OptionRendererCustomIcon}
                selectedValueObject={previewAccount}
                {...(isRoot ? {} : { valueContainerRenderer: AccountPickerValueContainer })}
                {...(isRoot ? {} : { arrowRenderer: () => null })}
                onChange={handleSelectedOption}
            />
        </BreadcrumbsPickerStep>
    );
};
